import { useState, memo, useMemo } from 'react';
import MessageButton from '../common/MessageButton';
import ProfileButton from '../common/ProfileButton';
import FavoriteButton from '../common/FavoriteButton';
import LazyCoverImage from '../common/LazyCoverImage';
import AvailabilityButton from '../common/AvailabilityButton';

// Helper function to decode HTML entities - moved outside component to avoid circular dependency
const decodeHtmlEntities = (str) => {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
};

// Memoize the member card to prevent unnecessary re-renders
const OptimizedMemberCard = memo(function MemberCard({ 
    member, 
    viewMode, 
    config, 
    onClick,
    onMouseEnter, 
    onMouseLeave, 
    isFavorite,
    onToggleFavorite,
    isPendingFavorite,
    isLoggedIn = true,
    ...props 
}) {
    // Memoize decoded display name to avoid re-computing
    const displayName = useMemo(() => {
        return decodeHtmlEntities(member.display_name);
    }, [member.display_name]);

    // Memoize intro text processing
    const processedIntroText = useMemo(() => {
        if (!member.intro_text) return null;
        
        const decoded = decodeHtmlEntities(member.intro_text);
        if (viewMode === 'compact') {
            const formCfg = config?.config || config || {};
            const limit = formCfg.intro_char_limit_compact || 80;
            return decoded.length > limit ? `${decoded.substring(0, limit)}...` : decoded;
        }
        // Standard/cards view: PHP already truncates at intro_char_limit, just decode
        return decoded;
    }, [member.intro_text, viewMode, config]);

    // Field IDs to display in member-actions instead of member-fields
    const actionFieldIds = ['912', '913'];

    // Memoize display fields processing (excluding action fields)
    const processedDisplayFields = useMemo(() => {
        if (!member.display_fields || member.display_fields.length === 0) {
            return null;
        }

        // Filter out fields 912 and 913 - they'll be shown in member-actions
        const listFields = member.display_fields.filter(field => {
            if (actionFieldIds.includes(String(field.field_id))) return false;
            if (viewMode === 'compact') return field.show_in_compact === true;
            return field.show_in_list !== false;
        });
        if (listFields.length === 0) return null;

        const maxFields = viewMode === 'compact' ? 2 : 4;
        return listFields.slice(0, maxFields).map((field, index) => {
            if (!field.value) return null;
            
            // Handle custom formatted fields (admin-set HTML content)
            if (field.type === 'custom_format') {
                return {
                    key: index,
                    fieldId: field.field_id,
                    label: decodeHtmlEntities(field.label),
                    hideLabel: field.hide_label || false,
                    value: field.value, // Keep raw HTML for custom formatted fields
                    isHtml: true
                };
            }
            
            // Handle regular fields with HTML entity decoding
            const fieldValue = Array.isArray(field.value) 
                ? field.value.map(v => decodeHtmlEntities(v)).join(', ') 
                : decodeHtmlEntities(field.value);

            return {
                key: index,
                fieldId: field.field_id,
                label: decodeHtmlEntities(field.label),
                hideLabel: field.hide_label || false,
                value: fieldValue,
                isHtml: false
            };
        }).filter(Boolean);
    }, [member.display_fields, viewMode]);

    // Memoize action fields (912 and 913) for display in member-actions
    const actionFields = useMemo(() => {
        if (!member.display_fields || member.display_fields.length === 0) {
            return null;
        }

        const fields = member.display_fields.filter(field => 
            actionFieldIds.includes(String(field.field_id)) && field.value
        );
        
        if (fields.length === 0) return null;

        return fields.map((field, index) => {
            if (field.type === 'custom_format') {
                return {
                    key: index,
                    fieldId: field.field_id,
                    value: field.value,
                    isHtml: true
                };
            }
            
            const fieldValue = Array.isArray(field.value) 
                ? field.value.map(v => decodeHtmlEntities(v)).join(', ') 
                : decodeHtmlEntities(field.value);

            return {
                key: index,
                fieldId: field.field_id,
                value: fieldValue,
                isHtml: false
            };
        });
    }, [member.display_fields]);

    // Memoize activity date formatting
    const formattedActivityDate = useMemo(() => {
        if (!member.last_activity) return null;
        return new Date(member.last_activity).toLocaleDateString();
    }, [member.last_activity]);

    // Determine if we're using avatar as the header image
    // The config prop is the top-level config object, form settings are in config.config
    const formConfig = config?.config || config || {};
    const coverImageSource = formConfig.cover_image_source || 'bp_cover';
    const isUsingAvatarAsCover = coverImageSource === 'bp_avatar';
    // Guard: some plugins store the cover image field as an HTML <img> tag — extract the src URL
    const rawCoverImage = member.cover_image;
    const coverImageUrl = (rawCoverImage && rawCoverImage.includes('<img'))
        ? (rawCoverImage.match(/src=["']([^"']+)["']/) || [])[1] || ''
        : rawCoverImage;
    const headerImageSrc = coverImageUrl || member.avatar_url;
    // Use 3:1 for avatars (shorter height), wider ratio for actual cover images
    const headerAspectRatio = isUsingAvatarAsCover ? '3:1' : (viewMode === 'cards' ? '16:7' : '3:1');




    const renderDisplayFields = () => {
        if (!processedDisplayFields) return null;

        return (
            <div className="member-fields">
                {processedDisplayFields.map((field) => (
                    <div 
                        key={field.key} 
                        className="member-field" 
                        data-field-id={field.fieldId}
                        id={`geo-xfield-${field.fieldId}`}
                    >
                        {!field.hideLabel && (
                            <span className="field-label">{field.label}:</span>
                        )}
                        {field.isHtml ? (
                            <span 
                                className="field-value field-html-content" 
                                dangerouslySetInnerHTML={{ __html: field.value }}
                            />
                        ) : (
                            <span className="field-value">{field.value}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderLocationInfo = () => {
        if (member.has_location) return null;

        return (
            <div className="location-info">
                📍 {member.location_text || 'No location set'}
            </div>
        );
    };

    // Memoize favorite status check
    const memberIsFavorite = useMemo(() => {
        return isFavorite(member.user_id);
    }, [isFavorite, member.user_id]);

    // Memoize pending status check  
    const memberIsPending = useMemo(() => {
        return isPendingFavorite(member.user_id);
    }, [isPendingFavorite, member.user_id]);

    return (
        <div 
            className={`member-card ${viewMode}-view`} 
            data-user-id={member.user_id}
            data-member-id={member.user_id}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...props}
        >
            {/* Cover Image Header - Full Width */}
            <div className={`member-cover-header ${isUsingAvatarAsCover ? 'avatar-as-cover' : ''}`}>
                <LazyCoverImage
                    src={headerImageSrc}
                    alt={displayName}
                    className={`member-cover-image ${isUsingAvatarAsCover ? 'is-avatar' : ''}`}
                    aspectRatio={headerAspectRatio}
                    isAvatar={isUsingAvatarAsCover}
                    avatarWidth={viewMode === 'compact' ? '90%' : '30%'}
                />
                {isLoggedIn && (
                    <div className="header-actions">
                        <FavoriteButton
                            userId={member.user_id}
                            isFavorite={memberIsFavorite}
                            onToggle={onToggleFavorite}
                            isPending={memberIsPending}
                            size="icon"
                            variant="overlay"
                        />
                    </div>
                )}
            </div>

            <div className="member-content">
                <div className="member-info">
                    <h3 className="member-name">{displayName}</h3>
                    
                    {processedIntroText && (
                        <div className="member-intro">
                            {processedIntroText}
                        </div>
                    )}

                    {renderLocationInfo()}
                    {renderDisplayFields()}

                    <div className="member-meta">
                        {formattedActivityDate && (
                            <span className="last-activity">
                                Active: {formattedActivityDate}
                            </span>
                        )}
                    </div>
                </div>

                <div className="member-actions">

                    <ProfileButton
                        profileUrl={member.profile_url}
                        memberName={displayName}
                        size={viewMode === 'compact' ? 'icon' : 'small'}
                        variant="outline"
                    />
                    {config?.gwVisitsCalendarEnabled && (
                        <AvailabilityButton
                            userId={member.user_id}
                            userName={displayName}
                            size={viewMode === 'compact' ? 'icon' : 'small'}
                            variant="outline"
                        />
                    )}
                    {actionFields && actionFields.map((field) => (
                        <span 
                            key={field.key}
                            className="action-field"
                            data-field-id={field.fieldId}
                        >
                            {field.isHtml ? (
                                <span dangerouslySetInnerHTML={{ __html: field.value }} />
                            ) : (
                                field.value
                            )}
                        </span>
                    ))}
                    {isLoggedIn && (
                        <MessageButton
                            recipientId={member.user_id}
                            recipientName={displayName}
                            size={viewMode === 'compact' ? 'icon' : 'small'}
                            variant="outline"
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

export default OptimizedMemberCard;
